import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Hourglass, Download, Link2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => `$${(n || 0).toFixed(2)}`;

const BUCKETS = [
  { key: 'not_due', label: 'Not Yet Due', cls: 'text-gray-600' },
  { key: 'd1_30', label: '1–30 Days', cls: 'text-amber-600' },
  { key: 'd31_60', label: '31–60 Days', cls: 'text-orange-600' },
  { key: 'd61_90', label: '61–90 Days', cls: 'text-red-600' },
  { key: 'd90_plus', label: '90+ Days', cls: 'text-red-700 font-bold' },
];

export default function AgingReport() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/invoices/reports/aging`)
      .then((res) => setData(res.data))
      .catch(() => toast.error('Failed to load aging report'))
      .finally(() => setLoading(false));
  }, []);

  const copyPortalLink = async (row) => {
    if (!row.customer_email) {
      toast.error('This customer has no email on their invoices — add one first');
      return;
    }
    try {
      const res = await axios.post(`${API}/api/public/portal/link`, {
        customer_email: row.customer_email,
        customer_name: row.customer_name,
      });
      const url = `${window.location.origin}/portal/${res.data.token}`;
      await navigator.clipboard.writeText(url);
      toast.success(`Portal link copied — send it to ${row.customer_name} so they can view and pay outstanding invoices`, { duration: 8000 });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create portal link');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-40" data-testid="aging-report-page">
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-6 flex items-center gap-4 flex-wrap">
          <Button variant="outline" onClick={() => navigate('/admin/billing')}><ArrowLeft size={18} /></Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Hourglass className="text-[#5c1a1a]" size={26} /> Receivables Aging Report
            </h1>
            <p className="text-gray-600 mt-1">Outstanding balances grouped by how long they've been overdue{data ? ` — as of ${data.as_of}` : ''}</p>
          </div>
          <a href={`${API}/api/invoices/reports/aging/pdf`} target="_blank" rel="noreferrer">
            <Button className="bg-[#5c1a1a] hover:bg-[#7a2525]" data-testid="aging-pdf-btn">
              <Download size={16} className="mr-2" /> Download PDF
            </Button>
          </a>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : !data || data.rows.length === 0 ? (
          <Card><CardContent className="text-center py-12 text-gray-500" data-testid="aging-empty">No outstanding invoices — everything is paid up! 🎉</CardContent></Card>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
              {BUCKETS.map((b) => (
                <Card key={b.key}>
                  <CardContent className="p-4">
                    <p className="text-xs text-gray-500 uppercase">{b.label}</p>
                    <p className={`text-xl font-bold ${b.cls}`} data-testid={`aging-total-${b.key}`}>{fmt(data.totals[b.key])}</p>
                  </CardContent>
                </Card>
              ))}
              <Card className="border-[#5c1a1a]">
                <CardContent className="p-4">
                  <p className="text-xs text-gray-500 uppercase">Total Outstanding</p>
                  <p className="text-xl font-bold text-[#5c1a1a]" data-testid="aging-grand-total">{fmt(data.totals.total)}</p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader><CardTitle className="text-lg">By Customer</CardTitle></CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50 text-left">
                        <th className="p-3">Customer</th>
                        {BUCKETS.map((b) => <th key={b.key} className="p-3 text-right">{b.label}</th>)}
                        <th className="p-3 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.rows.map((row) => (
                        <tr key={row.customer_name} className="border-b hover:bg-gray-50" data-testid={`aging-row-${row.customer_name}`}>
                          <td className="p-3">
                            <p className="font-semibold">{row.customer_name}
                              <button
                                onClick={() => copyPortalLink(row)}
                                className="ml-2 inline-flex items-center gap-1 text-xs font-normal text-[#5c1a1a] underline"
                                title="Copy customer portal link"
                                data-testid={`portal-link-${row.customer_name}`}
                              >
                                <Link2 size={12} /> Portal Link
                              </button>
                            </p>
                            <p className="text-xs text-gray-500">
                              {row.invoices.map((i) => (
                                <span key={i.id} className="mr-2">
                                  <a className="underline hover:text-[#5c1a1a]" href={`/admin/billing/invoice/${i.id}`}>{i.document_number}</a>
                                  {i.days_overdue > 0 ? ` (${i.days_overdue}d)` : ''}
                                </span>
                              ))}
                            </p>
                          </td>
                          {BUCKETS.map((b) => (
                            <td key={b.key} className={`p-3 text-right ${row[b.key] > 0 ? b.cls : 'text-gray-300'}`}>{row[b.key] > 0 ? fmt(row[b.key]) : '—'}</td>
                          ))}
                          <td className="p-3 text-right font-bold">{fmt(row.total)}</td>
                        </tr>
                      ))}
                      <tr className="bg-gray-100 font-bold">
                        <td className="p-3">TOTAL</td>
                        {BUCKETS.map((b) => <td key={b.key} className="p-3 text-right">{fmt(data.totals[b.key])}</td>)}
                        <td className="p-3 text-right text-[#5c1a1a]">{fmt(data.totals.total)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
